import React, { useEffect } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import { GetServerSideProps } from 'next';

interface MapPageProps {
  lat: number;
  lng: number;
  zoom: number;
  language?: string | null;
}

const MapPage: React.FC<MapPageProps> = ({ lat, lng, zoom, language }) => {
  useEffect(() => {
    // Dynamically load the external stylesheet
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://vytal-website.s3.amazonaws.com/city-map/static/css/main.8312be8f.css';
    document.head.appendChild(link);

    // Override all default styles to ensure full screen
    document.documentElement.style.margin = '0';
    document.documentElement.style.padding = '0';
    document.documentElement.style.height = '100%';
    document.documentElement.style.width = '100%';

    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.height = '100%';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    // Next.js wrapper
    const nextWrapper = document.getElementById('__next');
    if (nextWrapper) {
      nextWrapper.style.margin = '0';
      nextWrapper.style.padding = '0';
      nextWrapper.style.height = '100%';
      nextWrapper.style.width = '100%';
    }

    // Cleanup function to remove the stylesheet when component unmounts
    return () => {
      const existingLink = document.querySelector(`link[href="${link.href}"]`);
      if (existingLink) {
        document.head.removeChild(existingLink);
      }
    };
  }, []);

  return (
    <>
      <Head>
        <meta charSet="utf-8" />
        <meta name="lat" content={lat.toString()} />
        <meta name="lng" content={lng.toString()} />
        <meta name="zoom" content={zoom.toString()} />
        <meta name="showSearch" content="false" />
        <meta name="wolt" content="no" />
      </Head>

      <div
        id="root"
        style={{
          height: '100vh',
          width: '100vw',
          margin: 0,
          padding: 0,
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 1000,
          overflow: 'hidden',
        }}
      />

      <Script
        src="https://vytal-website.s3.eu-central-1.amazonaws.com/city-map/static/js/main.1b0f7067.js"
        strategy="afterInteractive"
        data-cookieconsent="ignore"
      />

      <noscript>You need to enable JavaScript to run this app.</noscript>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { lat, lng, zoom, language } = context.query;

  // Default coordinates (Berlin area) and zoom
  const DEFAULT_LAT = 52.5031139;
  const DEFAULT_LNG = 13.3975278;
  const DEFAULT_ZOOM = 12;

  // Parse numeric values and use defaults if not provided or invalid
  const parsedLat = lat ? parseFloat(lat as string) : DEFAULT_LAT;
  const parsedLng = lng ? parseFloat(lng as string) : DEFAULT_LNG;
  const parsedZoom = zoom ? parseFloat(zoom as string) : DEFAULT_ZOOM;

  return {
    props: {
      lat: !isNaN(parsedLat) ? parsedLat : DEFAULT_LAT,
      lng: !isNaN(parsedLng) ? parsedLng : DEFAULT_LNG,
      zoom: !isNaN(parsedZoom) ? parsedZoom : DEFAULT_ZOOM,
      language: (language as string) || null,
    },
  };
};

export default MapPage;
